import { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtMoneyByCcy, cycleShort, fmtDate } from "@/lib/fmt";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import { HalfYearMergePanel } from "./HalfYearMergePanel";
import { SettledThroughControl } from "./SettledThroughControl";
import { devError, devWarn } from "@/lib/log";

export default function AdminReconciliation() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // author_id whose detail is expanded
  const [draftFor, setDraftFor] = useState(null); // { author_id, cycle, subject, body, total_aud, author_email }
  const [drafting, setDrafting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [showSettled, setShowSettled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get("/admin/reconciliation");
      setRows(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      setLoadError(detail);
      devError("Reconciliation load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSyncData = async () => {
    const ok = window.confirm(
      "Sync the canonical reference dataset into this database?\n\n" +
      "This will:\n" +
      "  • Ingest every IngramSpark PDF in seed_data/\n" +
      "  • Import the WooCommerce historical orders\n" +
      "  • Import the 14 historical bank payments\n" +
      "  • Flag house orders correctly\n\n" +
      "It runs in the background and can take 1–3 minutes on production.\n" +
      "It's IDEMPOTENT — re-running it is completely safe."
    );
    if (!ok) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      await api.post("/admin/seed-reference-data");
      toast.info("Sync started in background — polling for progress…");
      // Poll status every 5s until finished or 5 minute timeout
      const maxPolls = 60;
      for (let i = 0; i < maxPolls; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data: status } = await api.get("/admin/seed-reference-data/status");
          if (!status.running) {
            if (status.error) {
              toast.error("Sync failed: " + status.error);
            } else {
              setSyncResult(status.stats);
              toast.success("Reference data sync complete");
              await load();
            }
            break;
          }
        } catch (pollErr) {
          devWarn("Sync status poll failed, retrying:", pollErr);
        }
      }
    } catch (e) {
      toast.error("Sync failed — " + (e?.response?.data?.detail || e?.message || "unknown error"));
    } finally {
      setSyncing(false);
    }
  };

  const totals = useMemo(() => {
    const sum = (key) => rows.reduce((acc, r) => {
      Object.entries(r[key] || {}).forEach(([c, v]) => { acc[c] = (acc[c] || 0) + v; });
      return acc;
    }, {});
    const actionable = rows.flatMap((r) => r.cycles.filter((c) => ["due_now", "overdue"].includes(c.status)));
    const owedPositive = rows.reduce((acc, r) => {
      Object.entries(r.owed_by_currency || {}).forEach(([c, v]) => { if (v > 0) acc[c] = (acc[c] || 0) + v; });
      return acc;
    }, {});
    return { paid: sum("paid_by_currency"), owed: owedPositive, actionable: actionable.length };
  }, [rows]);

  const openDraft = async (author_id, cycle) => {
    setDrafting(true);
    try {
      const { data } = await api.get(`/admin/email-draft?author_id=${author_id}&cycle=${cycle}`);
      setDraftFor(data);
    } catch {
      toast.error("Couldn't generate the email draft");
    } finally {
      setDrafting(false);
    }
  };

  const copy = async (text, label) => {
    const ok = await copyToClipboard(text);
    toast[ok ? "success" : "error"](ok ? `${label} copied` : "Couldn't copy — select & copy manually");
  };

  if (loading) return (
    <div className="space-y-8 p-2" data-testid="admin-reconciliation-page">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="hh-overline mb-3">Admin · Reconciliation</p>
          <h1 className="font-serif text-4xl text-hh-ink tracking-tight mb-2">What&rsquo;s been paid, what&rsquo;s owed</h1>
          <p className="text-hh-ink/70 text-sm">Loading reconciliation data…</p>
        </div>
        <button
          onClick={onSyncData}
          disabled={syncing}
          className="hh-btn-outline whitespace-nowrap"
          data-testid="sync-reference-data-btn"
        >
          {syncing ? "Syncing data…" : "Sync reference data"}
        </button>
      </header>
      {syncResult && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900" data-testid="sync-result">
          <p className="font-medium mb-1">Sync complete</p>
          <pre className="text-xs whitespace-pre-wrap font-mono">{JSON.stringify(syncResult, null, 2)}</pre>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="admin-reconciliation-page">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="hh-overline mb-3">Admin · Reconciliation</p>
          <h1 className="font-serif text-4xl text-hh-ink tracking-tight mb-2">What&rsquo;s been paid, what&rsquo;s owed</h1>
          <p className="text-hh-ink/70 text-sm max-w-3xl">
            Everything up to the &ldquo;settled through&rdquo; date is treated as paid in full. &ldquo;Owed now&rdquo; is each
            author&rsquo;s 95% share of sales after that date, less anything already paid for those quarters — shown in the
            currency it was earned, never converted. Each quarter falls due at the end of the following quarter and is
            processed within 30 days (e.g. Apr–Jun → due 30 Sep, process by 31 Oct).
          </p>
        </div>
        <button
          onClick={onSyncData}
          disabled={syncing}
          className="hh-btn-outline whitespace-nowrap"
          data-testid="sync-reference-data-btn"
          title="One-click: load every IngramSpark PDF, WC order and historical payment into this DB. Idempotent."
        >
          {syncing ? "Syncing data…" : "Sync reference data"}
        </button>
      </header>

      {syncResult && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900" data-testid="sync-result">
          <p className="font-medium mb-1">Sync complete</p>
          <pre className="text-xs whitespace-pre-wrap font-mono">{JSON.stringify(syncResult, null, 2)}</pre>
        </div>
      )}

      {loadError && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-900" data-testid="recon-load-error">
          <p className="font-medium mb-1">Couldn&rsquo;t load reconciliation data</p>
          <p className="text-xs font-mono mb-3">{loadError}</p>
          <p className="text-sm">
            This usually means the database hasn&rsquo;t been seeded yet on this environment. Click
            <strong> &ldquo;Sync reference data&rdquo;</strong> at the top right to load the IngramSpark PDFs,
            WooCommerce orders, and historical payments. Then this page will populate.
          </p>
          <button onClick={load} className="hh-btn-outline mt-3" data-testid="retry-recon">Retry</button>
        </div>
      )}

      <HalfYearMergePanel onMerged={load} />

      <SettledThroughControl value={rows[0]?.settled_through} onSaved={load} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard label="Paid to authors (all time)" value={<MoneyLines map={totals.paid} />} testid="recon-total-paid" />
        <SummaryCard label={`Owed now (after ${fmtDate(rows[0]?.settled_through)})`} value={<MoneyLines map={totals.owed} />} testid="recon-total-owed" />
        <SummaryCard label="Quarters to process now" value={totals.actionable} accent testid="recon-actionable-count" />
      </div>

      <label className="flex items-center gap-2 text-xs text-hh-ink/70 cursor-pointer w-fit" data-testid="toggle-settled-history">
        <input type="checkbox" checked={showSettled} onChange={(e) => setShowSettled(e.target.checked)} />
        Show settled quarters in author detail
      </label>

      <div className="rounded-2xl border border-hh-border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-hh-cream/60 text-hh-ink/70 text-left">
            <tr>
              <th className="px-5 py-3 font-semibold">Author</th>
              <th className="px-5 py-3 font-semibold text-right">Paid to date</th>
              <th className="px-5 py-3 font-semibold text-right">Owed now</th>
              <th className="px-5 py-3 font-semibold">Next step</th>
              <th className="px-5 py-3 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isOpen = open === r.author_id;
              return (
                <Fragment key={r.author_id}>
                  <tr className="border-t border-hh-border/60 hover:bg-hh-cream/30 cursor-pointer"
                      onClick={() => setOpen(isOpen ? null : r.author_id)}
                      data-testid={`recon-row-${r.author_id}`}>
                    <td className="px-5 py-3 font-medium text-hh-ink">{r.author_name}</td>
                    <td className="px-5 py-3 text-right tabular-nums" data-testid={`recon-paid-${r.author_id}`}><MoneyLines map={r.paid_by_currency} /></td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold" data-testid={`recon-owed-${r.author_id}`}><MoneyLines map={r.owed_by_currency} signed /></td>
                    <td className="px-5 py-3" data-testid={`recon-next-step-${r.author_id}`}><NextStep cycles={r.cycles} /></td>
                    <td className="px-5 py-3 text-right">
                      <button className="text-xs px-2.5 py-1.5 rounded-md bg-hh-violet/10 hover:bg-hh-violet/20 text-hh-violet font-medium"
                              data-testid={`expand-${r.author_id}`}>
                        {isOpen ? "Hide" : "Detail"}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-hh-cream/20">
                      <td colSpan={5} className="px-5 py-4">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <p className="hh-overline mb-2">Quarterly earnings</p>
                            <table className="w-full text-xs">
                              <thead className="text-hh-ink/60">
                                <tr>
                                  <th className="text-left py-1.5">Cycle</th>
                                  <th className="text-right pl-3">Units</th>
                                  <th className="text-right pl-4">Earned</th>
                                  <th className="text-right pl-4">Paid</th>
                                  <th className="text-left pl-3">Status</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {r.cycles.filter((c) => showSettled || c.status !== "settled").length === 0 && (
                                  <tr><td colSpan={6} className="text-hh-ink/50 py-2">Nothing since the settled-through date</td></tr>
                                )}
                                {r.cycles.filter((c) => showSettled || c.status !== "settled").map((c) => (
                                  <tr key={c.cycle} className="border-t border-hh-border/40 align-top" data-testid={`recon-cycle-${r.author_id}-${c.cycle}`}>
                                    <td className="py-1.5 whitespace-nowrap">{cycleShort(c.cycle)}</td>
                                    <td className="text-right tabular-nums py-1.5 pl-3">{c.units}</td>
                                    <td className="text-right tabular-nums py-1.5 pl-4 whitespace-nowrap">{c.by_currency.map((x) => <div key={x.currency}>{fmtMoney(x.author_share, x.currency)}</div>)}</td>
                                    <td className="text-right tabular-nums py-1.5 pl-4 whitespace-nowrap">{c.by_currency.map((x) => <div key={x.currency}>{x.paid ? fmtMoney(x.paid, x.currency) : "—"}</div>)}</td>
                                    <td className="pl-3 py-1.5"><StatusBadge c={c} testid={`recon-status-${r.author_id}-${c.cycle}`} /></td>
                                    <td className="text-right">
                                      <button
                                        className="text-xs text-hh-violet hover:underline whitespace-nowrap pl-3"
                                        onClick={(e) => { e.stopPropagation(); openDraft(r.author_id, c.cycle); }}
                                        data-testid={`draft-email-${r.author_id}-${c.cycle}`}
                                      >Draft email →</button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div>
                            <p className="hh-overline mb-2">Payments paid out</p>
                            <table className="w-full text-xs">
                              <thead className="text-hh-ink/60">
                                <tr>
                                  <th className="text-left py-1.5">Date</th>
                                  <th className="text-left">Cycle</th>
                                  <th className="text-left">Kind</th>
                                  <th className="text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {r.payments.length === 0 && <tr><td colSpan={4} className="text-hh-ink/50 py-2">No payments yet</td></tr>}
                                {r.payments.map((p) => (
                                  <tr key={p.id} className="border-t border-hh-border/40" data-testid={`recon-payment-${p.id}`}>
                                    <td className="py-1.5">{p.paid_date ? fmtDate(p.paid_date) : (p.status === "paid" ? "—" : "Pending")}</td>
                                    <td>{cycleShort(p.cycle)}</td>
                                    <td className="capitalize">{p.kind}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(p.amount, p.currency)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {draftFor && (
        <div className="fixed inset-0 bg-hh-ink/40 backdrop-blur-sm flex items-center justify-center p-6 z-50" onClick={() => setDraftFor(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()} data-testid="email-draft-modal">
            <div className="px-6 py-4 border-b border-hh-border flex items-center justify-between">
              <div>
                <p className="hh-overline">Email draft · {cycleShort(draftFor.cycle)}</p>
                <h2 className="font-serif text-xl text-hh-ink mt-1">{draftFor.author_name} — {fmtMoneyByCcy(draftFor.totals_by_currency)}</h2>
              </div>
              <button onClick={() => setDraftFor(null)} className="text-hh-ink/60 hover:text-hh-ink text-2xl leading-none" data-testid="close-draft-modal">×</button>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto">
              <Field label="To" value={draftFor.author_email || "(no email on file)"} onCopy={() => copy(draftFor.author_email || "", "Email address")} testid="copy-to" />
              <Field label="Subject" value={draftFor.subject} onCopy={() => copy(draftFor.subject, "Subject")} testid="copy-subject" />
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="hh-overline">Body</label>
                  <button className="text-xs px-2 py-1 rounded bg-hh-violet/10 hover:bg-hh-violet/20 text-hh-violet" onClick={() => copy(draftFor.body, "Body")} data-testid="copy-body">Copy body</button>
                </div>
                <textarea
                  className="w-full h-[420px] p-3 border border-hh-border rounded-lg text-sm font-mono text-hh-ink"
                  value={draftFor.body}
                  onChange={(e) => setDraftFor({ ...draftFor, body: e.target.value })}
                  data-testid="email-body-textarea"
                />
              </div>
              <button
                onClick={() => copy(`Subject: ${draftFor.subject}\nTo: ${draftFor.author_email || ""}\n\n${draftFor.body}`, "Whole email")}
                className="hh-btn-primary w-full"
                data-testid="copy-everything"
              >Copy everything (To + Subject + Body)</button>
            </div>
          </div>
        </div>
      )}
      {drafting && <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-40 text-white">Generating draft…</div>}
    </div>
  );
}

function SummaryCard({ label, value, accent, testid }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? "border-hh-violet/30 bg-hh-violet/5" : "border-hh-border bg-white"}`} data-testid={testid}>
      <p className="hh-overline">{label}</p>
      <div className={`font-serif text-2xl mt-2 ${accent ? "text-hh-violet" : "text-hh-ink"}`}>{value}</div>
    </div>
  );
}

function MoneyLines({ map, signed }) {
  const entries = Object.entries(map || {}).filter(([, v]) => Math.abs(v) >= 0.005);
  if (entries.length === 0) return <span className="text-hh-ink/50">—</span>;
  return entries.map(([ccy, v]) => {
    if (signed && v < 0) {
      return <div key={ccy} className="text-amber-600" title="Paid more than the sales on record — check for missing sales reports">−{fmtMoney(-v, ccy)}</div>;
    }
    return <div key={ccy}>{fmtMoney(v, ccy)}</div>;
  });
}

const STATUS_STYLES = {
  paid: ["Paid", "bg-emerald-50 text-emerald-700"],
  settled: ["Settled", "bg-emerald-50/60 text-emerald-700/70"],
  nil: ["Nothing to pay", "bg-hh-cream text-hh-ink/50"],
  in_progress: ["Quarter in progress", "bg-hh-cream text-hh-ink/70"],
  not_yet_due: ["Not yet due", "bg-hh-cream text-hh-ink/70"],
  due_now: ["Process now", "bg-amber-50 text-amber-700"],
  overdue: ["Overdue", "bg-red-50 text-red-700"],
};

function StatusBadge({ c, testid }) {
  const [base, cls] = STATUS_STYLES[c.status] || [c.status, ""];
  const label = c.part_paid ? `Part paid · ${base.toLowerCase()}` : base;
  const hint = ["paid", "nil", "settled"].includes(c.status) ? "" : `due ${fmtDate(c.due_date)} · process by ${fmtDate(c.process_by)}`;
  return (
    <div data-testid={testid}>
      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{label}</span>
      {hint && <div className="text-[10px] text-hh-ink/50 mt-0.5">{hint}</div>}
    </div>
  );
}

function NextStep({ cycles }) {
  const urgent = cycles.find((c) => c.status === "overdue") || cycles.find((c) => c.status === "due_now");
  if (urgent) return <StatusBadge c={urgent} />;
  const upcoming = cycles.find((c) => ["not_yet_due", "in_progress"].includes(c.status));
  if (upcoming) return <span className="text-xs text-hh-ink/60">Next: {cycleShort(upcoming.cycle)} by {fmtDate(upcoming.process_by)}</span>;
  return <span className="text-xs text-emerald-700">Up to date</span>;
}

function Field({ label, value, onCopy, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="hh-overline">{label}</label>
        <button className="text-xs px-2 py-1 rounded bg-hh-violet/10 hover:bg-hh-violet/20 text-hh-violet" onClick={onCopy} data-testid={testid}>Copy</button>
      </div>
      <div className="px-3 py-2 border border-hh-border rounded-lg text-sm text-hh-ink bg-hh-cream/30">{value}</div>
    </div>
  );
}
